import React, { useState } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SoporteTab({ userData, refreshData }) {
  const { getToken } = useAdminAuth();
  const { user, tickets, activity } = userData;
  const [showCreateTicketModal, setShowCreateTicketModal] = useState(false);
  const [showTicketDetailModal, setShowTicketDetailModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleCreateTicket = () => {
    setShowCreateTicketModal(true);
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowTicketDetailModal(true);
  };

  const handleSendMessage = async () => {
    const message = prompt('Escribe el mensaje para el usuario:');
    if (!message) return;

    setIsProcessing(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${user.id}/notify`,
        {
          template_id: 'admin_message',
          params: { message }
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      alert('Mensaje enviado por email');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al enviar mensaje');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResendVerification = async () => {
    if (!window.confirm('¿Reenviar email de verificación?')) return;

    setIsProcessing(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${user.id}/resend-verification`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      alert('Email de verificación reenviado');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al reenviar verificación');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResetPassword = async () => {
    if (!window.confirm('¿Enviar email para resetear contraseña?')) return;

    setIsProcessing(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${user.id}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      alert('Email de reseteo enviado');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al enviar email de reseteo');
    } finally {
      setIsProcessing(false);
    }
  };

  const getActivityIcon = (action) => {
    const icons = {
      'create': '➕',
      'update': '✏️',
      'delete': '🗑️',
      'scholarship_applied': '🎓',
      'payment_completed': '💳',
      'payment_link_generated': '🔗',
      'activated': '🚀',
      'suspended': '⚠️',
      'community_grant': '👥',
      'community_revoke': '❌',
      'certificate_issued': '🏆',
      'attendance_marked': '✅',
      'project_updated': '📝',
      'manual_payment_registered': '💰',
    };
    return icons[action] || '📌';
  };

  const formatActivityDescription = (act) => {
    const descriptions = {
      'create': 'Usuario registrado',
      'update': 'Perfil actualizado',
      'scholarship_applied': `Beca aplicada: ${act.details?.percent || 0}%`,
      'payment_completed': `Pago completado: $${act.details?.amount || 0}`,
      'payment_link_generated': 'Link de pago generado',
      'activated': 'Cuenta activada',
      'suspended': 'Cuenta suspendida',
      'community_grant': 'Acceso a comunidad otorgado',
      'community_revoke': 'Acceso a comunidad revocado',
      'certificate_issued': 'Certificado emitido',
      'attendance_marked': `Asistencia marcada: ${act.details?.attended ? 'Presente' : 'Ausente'}`,
      'project_updated': `Proyecto ${act.details?.status || 'actualizado'}`,
      'manual_payment_registered': `Pago manual: $${act.details?.amount || 0}`,
    };
    return descriptions[act.action] || act.action;
  };

  // Stats
  const openTickets = tickets?.filter(t => t.status === 'open').length || 0;
  const inProgressTickets = tickets?.filter(t => t.status === 'in_progress').length || 0;
  const closedTickets = tickets?.filter(t => t.status === 'closed').length || 0;

  return (
    <div className="space-y-6">
      {/* Tickets Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-yellow-500/10 rounded-lg p-4 border border-yellow-500/30">
          <div className="text-xs text-gray-400 mb-1">Tickets Abiertos</div>
          <div className="text-2xl font-bold text-yellow-400">{openTickets}</div>
        </div>
        <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/30">
          <div className="text-xs text-gray-400 mb-1">En Progreso</div>
          <div className="text-2xl font-bold text-blue-400">{inProgressTickets}</div>
        </div>
        <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/30">
          <div className="text-xs text-gray-400 mb-1">Cerrados</div>
          <div className="text-2xl font-bold text-green-400">{closedTickets}</div>
        </div>
      </div>

      {/* Support Tickets */}
      <div className="bg-black/30 rounded-lg p-6 border border-[#1a4d5c]/30">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-white">Tickets de Soporte ({tickets?.length || 0})</h4>
          <button
            onClick={handleCreateTicket}
            className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors border border-blue-500/50 text-sm"
          >
            + Crear Ticket
          </button>
        </div>
        {tickets && tickets.length > 0 ? (
          <div className="space-y-3">
            {tickets.map((ticket) => (
              <div
                key={ticket.id}
                onClick={() => handleViewTicket(ticket)}
                className="p-4 bg-black/50 rounded-lg border border-[#1a4d5c]/30 hover:bg-[#1a4d5c]/10 transition-colors cursor-pointer"
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h5 className="text-white font-medium">{ticket.subject || 'Sin asunto'}</h5>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        ticket.status === 'open' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                        ticket.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                        'bg-green-500/20 text-green-400 border border-green-500/30'
                      }`}>
                        {ticket.status}
                      </span>
                      {ticket.priority && (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          ticket.priority === 'high' ? 'bg-red-500/20 text-red-400' :
                          ticket.priority === 'medium' ? 'bg-orange-500/20 text-orange-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {ticket.priority}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-400 text-sm line-clamp-2">{ticket.description || 'Sin descripción'}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
                  <span>ID: {ticket.id}</span>
                  <span>Creado: {new Date(ticket.created_at).toLocaleDateString('es-ES')}</span>
                  {ticket.assigned_to && <span>Asignado: {ticket.assigned_to}</span>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-8">No hay tickets de soporte</p>
        )}
      </div>

      {/* Activity Timeline */}
      <div className="bg-black/30 rounded-lg p-6 border border-[#1a4d5c]/30">
        <h4 className="font-semibold text-white mb-4">Timeline de Actividad ({activity?.length || 0})</h4>
        {activity && activity.length > 0 ? (
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-[#1a4d5c]/30"></div>
            
            <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
              {activity.slice(0, 50).map((act, index) => (
                <div key={index} className="relative flex gap-4">
                  {/* Icon */}
                  <div className="relative z-10 w-12 h-12 rounded-full bg-[#1a4d5c]/30 border-2 border-[#1a4d5c]/50 flex items-center justify-center flex-shrink-0">
                    <span className="text-lg">{getActivityIcon(act.action)}</span>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 pt-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="text-white font-medium">{formatActivityDescription(act)}</div>
                        <div className="text-sm text-gray-400 mt-1">
                          {act.resource_type && `${act.resource_type}: ${act.resource_id}`}
                        </div>
                        {act.admin_email && (
                          <div className="text-xs text-gray-500 mt-1">
                            Por: {act.admin_email}
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 text-right">
                        {new Date(act.timestamp).toLocaleString('es-ES', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-gray-400 text-center py-8">Sin actividad registrada</p>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-black/30 rounded-lg p-6 border border-[#1a4d5c]/30">
        <h4 className="font-semibold text-white mb-4">Acciones Rápidas</h4>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={handleCreateTicket}
            disabled={isProcessing}
            className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors text-sm font-medium disabled:opacity-50"
          >
            🎫 Crear Ticket
          </button>
          <button
            onClick={handleSendMessage}
            disabled={isProcessing}
            className="px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors text-sm font-medium disabled:opacity-50"
          >
            📧 Enviar Mensaje
          </button>
          <button
            onClick={handleResendVerification}
            disabled={isProcessing}
            className="px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors text-sm font-medium disabled:opacity-50"
          >
            🔄 Reenviar Verificación
          </button>
          <button
            onClick={handleResetPassword}
            disabled={isProcessing}
            className="px-4 py-2 bg-orange-500/20 text-orange-400 rounded-lg hover:bg-orange-500/30 transition-colors text-sm font-medium disabled:opacity-50"
          >
            🔑 Resetear Contraseña
          </button>
        </div>
      </div>

      {/* Modals */}
      {showCreateTicketModal && (
        <CreateTicketModal
          userId={user.id}
          onClose={() => setShowCreateTicketModal(false)}
          onSuccess={() => {
            setShowCreateTicketModal(false);
            refreshData();
          }}
        />
      )}

      {showTicketDetailModal && selectedTicket && (
        <TicketDetailModal
          ticket={selectedTicket}
          userId={user.id}
          onClose={() => {
            setShowTicketDetailModal(false);
            setSelectedTicket(null);
          }}
          onSuccess={() => {
            setShowTicketDetailModal(false);
            setSelectedTicket(null);
            refreshData();
          }}
        />
      )}
    </div>
  );
}

// Create Ticket Modal
function CreateTicketModal({ userId, onClose, onSuccess }) {
  const { getToken } = useAdminAuth();
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [category, setCategory] = useState('general');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim()) {
      alert('Por favor ingresa un asunto');
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/tickets`,
        {
          user_id: userId,
          subject,
          description,
          priority,
          category,
          status: 'open'
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      alert('Ticket creado correctamente');
      onSuccess();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al crear ticket');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="bg-[#0A0A0A] rounded-lg max-w-2xl w-full p-6 border border-[#1a4d5c]/30">
        <h3 className="text-xl font-bold text-white mb-4">Crear Ticket de Soporte</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-2">Asunto *</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Problema con acceso a sesiones..."
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-300 mb-2">Prioridad</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white"
              >
                <option value="low">Baja</option>
                <option value="medium">Media</option>
                <option value="high">Alta</option>
                <option value="urgent">Urgente</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-2">Categoría</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white"
              >
                <option value="general">General</option>
                <option value="technical">Técnico</option>
                <option value="billing">Facturación</option>
                <option value="content">Contenido</option>
                <option value="community">Comunidad</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-2">Descripción</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows="6"
              placeholder="Describe el problema en detalle..."
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
            />
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#F4E4B7] text-black rounded-lg disabled:opacity-50"
            >
              {isLoading ? 'Creando...' : 'Crear Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Ticket Detail Modal
function TicketDetailModal({ ticket, userId, onClose, onSuccess }) {
  const { getToken } = useAdminAuth();
  const [status, setStatus] = useState(ticket.status);
  const [response, setResponse] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleUpdateTicket = async () => {
    setIsLoading(true);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/tickets/${ticket.id}`,
        { status, response },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      alert('Ticket actualizado');
      onSuccess();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al actualizar ticket');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="bg-[#0A0A0A] rounded-lg max-w-2xl w-full p-6 border border-[#1a4d5c]/30 max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold text-white mb-4">Detalle del Ticket</h3>
        
        <div className="space-y-4">
          {/* Ticket Info */}
          <div className="bg-black/50 rounded-lg p-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h4 className="text-white font-semibold text-lg">{ticket.subject}</h4>
                <p className="text-gray-400 text-sm">ID: {ticket.id}</p>
              </div>
              <span className={`px-3 py-1 rounded text-xs font-medium ${
                ticket.priority === 'high' || ticket.priority === 'urgent' ? 'bg-red-500/20 text-red-400' :
                ticket.priority === 'medium' ? 'bg-orange-500/20 text-orange-400' :
                'bg-gray-500/20 text-gray-400'
              }`}>
                {ticket.priority}
              </span>
            </div>
            <p className="text-gray-300 text-sm whitespace-pre-wrap">{ticket.description}</p>
            <div className="text-xs text-gray-500 mt-3">
              Creado: {new Date(ticket.created_at).toLocaleString('es-ES')}
            </div>
          </div>

          {/* Update Status */}
          <div>
            <label className="block text-sm text-gray-300 mb-2">Estado del Ticket</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white"
            >
              <option value="open">Abierto</option>
              <option value="in_progress">En Progreso</option>
              <option value="closed">Cerrado</option>
            </select>
          </div>

          {/* Response */}
          <div>
            <label className="block text-sm text-gray-300 mb-2">Respuesta/Nota</label>
            <textarea
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              rows="4"
              placeholder="Escribe tu respuesta..."
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
            >
              Cerrar
            </button>
            <button
              onClick={handleUpdateTicket}
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#F4E4B7] text-black rounded-lg disabled:opacity-50"
            >
              {isLoading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}